/**
 * StrategyHeader.tsx — Strategia header card + stat bar with the live
 * MarginGauge. Every value is server-computed (StrategyTotals): CME cost,
 * planned cost, margin € (incl. savings from accepted quotes) and the margin %
 * vs the target. The margin is flagged provisional — the exact formula is still
 * pending the team's decision — so it is never presented as final here.
 */
import { I, type IconComponent } from "../../components/icons";
import { fmtEur } from "../../lib/format";
import type { StrategyTotals } from "../../lib/strategyApi";

function Stat({
  icon: Ico,
  label,
  value,
  sub,
  last,
}: {
  icon: IconComponent;
  label: string;
  value: string;
  sub?: string;
  last?: boolean;
}): JSX.Element {
  return (
    <div style={{ padding: "14px 18px", borderRight: last ? "none" : "1px solid var(--c-line)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--c-ink-3)",
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        <Ico size={11} /> {label}
      </div>
      <div
        style={{
          fontSize: 17,
          fontWeight: 600,
          marginTop: 4,
          fontFamily: "var(--font-mono)",
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function MarginGauge({
  value,
  target,
  provisional,
}: {
  value: number;
  target: number;
  provisional: boolean;
}): JSX.Element {
  const ok = value >= target;
  // The backend gives a %; scale the bar so the target sits mid-track and a
  // negative/over-target margin still renders inside bounds.
  const scaleMax = Math.max(target * 2, value * 1.2, 1);
  const barPct = Math.max(0, Math.min(100, (value / scaleMax) * 100));
  const tickPct = Math.max(0, Math.min(100, (target / scaleMax) * 100));
  return (
    <div style={{ padding: "14px 18px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "var(--c-ink-3)",
        }}
      >
        Margine attuale
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          fontFamily: "var(--font-mono)",
          marginTop: 4,
          letterSpacing: "-0.01em",
          color: ok ? "var(--c-ok)" : "var(--c-ink)",
        }}
      >
        {value.toFixed(1)}%
      </div>
      <div
        style={{
          height: 4,
          background: "var(--c-line)",
          borderRadius: 2,
          marginTop: 6,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: `${tickPct}%`,
            top: -2,
            width: 1.5,
            height: 8,
            background: "var(--c-ink-3)",
          }}
        />
        <div
          style={{
            width: `${barPct}%`,
            height: "100%",
            background: ok ? "var(--c-ok)" : "var(--c-warn)",
            transition: "width var(--tr-med)",
          }}
        />
      </div>
      <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 4 }}>
        Target: {target.toFixed(1)}%
      </div>
      {provisional && (
        <span
          className="pill pill-warn"
          title={
            "La formula del margine è ancora in via di conferma dal team — questo valore è provvisorio, non definitivo."
          }
          style={{ marginTop: 8, height: 18, fontSize: 10 }}
        >
          <I.AlertC size={10} /> Formula in via di conferma
        </span>
      )}
    </div>
  );
}

export function StrategyHeader({ totals }: { totals: StrategyTotals }): JSX.Element {
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div
        style={{
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          borderBottom: "1px solid var(--c-line)",
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 8,
            background: "var(--c-brand-soft)",
            color: "var(--c-brand-ink)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <I.Strategy size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0, letterSpacing: "-0.01em" }}>
            Strategia &amp; Margini
          </h1>
          <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginTop: 3 }}>
            {
              "Decidi cosa internalizzare ed esternalizzare. Le offerte accettate aggiornano il margine."
            }
          </div>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          borderTop: "1px solid var(--c-line)",
        }}
      >
        <Stat icon={I.Euro} label="Costo CME" value={fmtEur(totals.cme)} />
        <Stat
          icon={I.Tools}
          label="Costo previsto"
          value={fmtEur(totals.expected)}
          sub="make/buy pianificato"
        />
        <Stat
          icon={I.ArrowDown}
          label="Delta (margine €)"
          value={fmtEur(totals.marginEur)}
          sub={`incl. ${fmtEur(totals.acceptedSavings)} da offerte`}
        />
        <MarginGauge
          value={totals.marginPct}
          target={totals.targetPct}
          provisional={totals.marginProvisional}
        />
      </div>
    </div>
  );
}
